import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

/*
  SuperAdmin.js — Admin panel
  Matches UserDashboard / Profile visual language:
  - Same font stack (Syne, DM Sans, JetBrains Mono)
  - Same light surface + cyan accent tokens
  - Clean sidebar nav, collapsible on mobile
  Admin privileges:
  - Edit any user field (superadmin: full access including role)
  - Change primaryFlapid with uniqueness check
  - Set emergencyContacts, accessories, tagMeta on any user
  - Manage FlapCare tokens
  - Product + unit inventory
  - Orders with full timeline
  - Support ticket replies
*/

/* ── Design tokens — same as UserDashboard.js ─────────────────────────────── */
const T = {
  bg:     '#f0f3ff',
  surface:'#ffffff',
  border: 'rgba(0,0,0,0.08)',
  dim:    '#e5e7eb',
  text:   '#111827',
  muted:  '#6b7280',
  cyan:   '#38bdf8',
  teal:   '#2dd4bf',
  green:  '#34d399',
  amber:  '#f59e0b',
  red:    '#f87171',
  purple: '#818cf8',
};

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');`;

function rgb(hex) {
  const c=(hex||'#38bdf8').replace('#','');
  const f=c.length===3?c.split('').map(x=>x+x).join(''):c;
  const n=parseInt(f,16);
  return `${(n>>16)&255},${(n>>8)&255},${n&255}`;
}

const fmtDate = d => d?new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}):'—';
const fmtDT   = d => d?new Date(d).toLocaleString('en-US',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}):'—';
const fmtAmt  = n => `$${((n||0)/100).toFixed(2)}`;

const STATUS_COLOR = {
  pending:'#f59e0b',confirmed:'#38bdf8',processing:'#818cf8',shipped:'#34d399',
  delivered:'#10b981',cancelled:'#f87171',refunded:'#64748b',
  open:'#f87171',in_progress:'#f59e0b',waiting:'#38bdf8',resolved:'#34d399',closed:'#475569',
  active:'#34d399',suspended:'#f87171',banned:'#ef4444',
  low:'#475569',medium:'#38bdf8',high:'#f59e0b',urgent:'#f87171',
  free:'#6b7280',pro:'#38bdf8',business:'#f59e0b',
};

/* ── Reusable components ──────────────────────────────────────────────────── */
function Pill({ label, color='#6b7280' }) {
  return (
    <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:99,
      background:`rgba(${rgb(color)},.1)`,color,letterSpacing:.8,textTransform:'uppercase',
      display:'inline-block',border:`1px solid rgba(${rgb(color)},.15)`,
      fontFamily:'JetBrains Mono,monospace'}}>
      {label}
    </span>
  );
}

function Card({ children, style={} }) {
  return (
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,
      padding:'18px 20px',boxShadow:'0 1px 6px rgba(0,0,0,0.04)',...style}}>
      {children}
    </div>
  );
}

function Stat({ icon, label, value, sub, color=T.amber }) {
  return (
    <Card style={{position:'relative',overflow:'hidden'}}>
      <div style={{position:'absolute',inset:0,background:`radial-gradient(ellipse at top right,rgba(${rgb(color)},.07),transparent 65%)`}}/>
      <div style={{position:'absolute',top:12,right:14,fontSize:20,opacity:.15}}>{icon}</div>
      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:'uppercase',
        letterSpacing:1.4,marginBottom:6,fontFamily:'JetBrains Mono,monospace'}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color,fontFamily:'Syne,sans-serif',letterSpacing:-.5}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>{sub}</div>}
    </Card>
  );
}

function Modal({ title, onClose, children, width=560 }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1000,
      display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(6px)',padding:16}}>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,
        width:`min(${width}px,95vw)`,maxHeight:'90vh',overflowY:'auto',
        boxShadow:'0 24px 64px rgba(0,0,0,0.15)'}}>
        <div style={{padding:'18px 22px',borderBottom:`1px solid ${T.dim}`,
          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:15,fontWeight:700,color:T.text,fontFamily:'Syne,sans-serif'}}>{title}</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,
            border:`1px solid ${T.dim}`,background:'rgba(0,0,0,0.03)',color:T.muted,
            cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
        </div>
        <div style={{padding:'18px 22px'}}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type='text', options, rows, placeholder }) {
  const base = {width:'100%',background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
    borderRadius:9,padding:'9px 12px',color:T.text,fontSize:13,outline:'none',
    boxSizing:'border-box',fontFamily:'DM Sans,sans-serif',marginTop:4};
  return (
    <div style={{marginBottom:12}}>
      <label style={{fontSize:10,color:T.muted,textTransform:'uppercase',letterSpacing:1,
        display:'block',fontFamily:'JetBrains Mono,monospace'}}>{label}</label>
      {options?(
        <select value={value} onChange={e=>onChange(e.target.value)} style={base}>
          {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
        </select>
      ):rows?(
        <textarea value={value} onChange={e=>onChange(e.target.value)} rows={rows}
          placeholder={placeholder} style={{...base,resize:'vertical'}}/>
      ):(
        <input type={type} value={value||''} onChange={e=>onChange(e.target.value)}
          placeholder={placeholder} style={base}/>
      )}
    </div>
  );
}

function Table({ cols, rows, onRowClick }) {
  return (
    <div style={{overflowX:'auto',borderRadius:12,border:`1px solid ${T.dim}`}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
        <thead>
          <tr style={{background:'rgba(0,0,0,0.02)'}}>
            {cols.map(c=>(
              <th key={c.key} style={{padding:'10px 14px',textAlign:'left',fontSize:10,fontWeight:700,
                textTransform:'uppercase',letterSpacing:1.2,color:T.muted,
                borderBottom:`1px solid ${T.dim}`,whiteSpace:'nowrap',
                fontFamily:'JetBrains Mono,monospace'}}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length===0&&(
            <tr><td colSpan={cols.length} style={{textAlign:'center',padding:'40px 0',color:T.muted,fontSize:13}}>
              No records found</td></tr>
          )}
          {rows.map((row,i)=>(
            <tr key={row._id||i} onClick={()=>onRowClick?.(row)}
              style={{borderBottom:`1px solid ${T.dim}`,cursor:onRowClick?'pointer':'default',
                transition:'background .12s'}}
              onMouseEnter={e=>{if(onRowClick)e.currentTarget.style.background='rgba(56,189,248,0.04)';}}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              {cols.map(c=>(
                <td key={c.key} style={{padding:'12px 14px',color:T.text,verticalAlign:'middle'}}>
                  {c.render?c.render(row[c.key],row):(row[c.key]??'—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Btn({ children, onClick, color=T.amber, outline=false, style={}, disabled=false }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{padding:'9px 18px',borderRadius:10,border:outline?`1px solid rgba(${rgb(color)},.3)`:'none',
        background:outline?`rgba(${rgb(color)},.07)`:color==='danger'?T.red
          :`linear-gradient(135deg,${color},${color}cc)`,
        color:outline?color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13,
        fontFamily:'DM Sans,sans-serif',opacity:disabled?.6:1,...style}}>
      {children}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: Overview
   ════════════════════════════════════════════════════════════════════════════ */
function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/admin/overview').then(r=>setData(r.data)).catch(()=>{}); }, []);
  if (!data) return <div style={{color:T.muted,textAlign:'center',paddingTop:60}}>Loading…</div>;
  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12,marginBottom:24}}>
        <Stat icon="👥" label="Total Users"     value={data.users?.total||0}         sub={`+${data.users?.newToday||0} today`} color={T.cyan}/>
        <Stat icon="📦" label="Total Orders"    value={data.orders?.total||0}        sub={`${data.orders?.pending||0} pending`} color={T.teal}/>
        <Stat icon="💰" label="Revenue"         value={fmtAmt(data.orders?.revenue)} color={T.green}/>
        <Stat icon="🎫" label="Open Tickets"    value={data.support?.open||0}        sub={`${data.support?.urgent||0} urgent`} color={T.red}/>
        <Stat icon="🛍️" label="Products"        value={data.products?.active||0}     sub={`${data.products?.total||0} total`} color={T.purple}/>
        <Stat icon="📦" label="Units Available" value={data.inventory?.unassigned||0} sub={`${data.inventory?.total||0} total`} color={T.green}/>
        <Stat icon="💳" label="Units Assigned"  value={data.inventory?.assigned||0}  sub={`${data.inventory?.returned||0} returned`} color={T.amber}/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14,fontFamily:'Syne,sans-serif'}}>
            📈 Orders — Last 30 Days
          </div>
          {(data.dailyTrend||[]).length>0?(
            <div style={{display:'flex',alignItems:'flex-end',gap:3,height:60}}>
              {(data.dailyTrend||[]).map((d,i)=>{
                const max=Math.max(...(data.dailyTrend||[]).map(x=>x.orders),1);
                return <div key={i} title={`${d._id}: ${d.orders}`}
                  style={{flex:1,background:`rgba(${rgb(T.amber)},${.2+.8*(d.orders/max)})`,
                    borderRadius:'3px 3px 0 0',minHeight:3,
                    height:`${Math.max(5,(d.orders/max)*100)}%`}}/>;
              })}
            </div>
          ):<div style={{color:T.muted,fontSize:12}}>No data</div>}
        </Card>
        <Card>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14,fontFamily:'Syne,sans-serif'}}>
            🕐 Recent Orders
          </div>
          {(data.recentOrders||[]).slice(0,6).map(o=>(
            <div key={o._id} style={{display:'flex',justifyContent:'space-between',
              padding:'7px 0',borderBottom:`1px solid ${T.dim}`,fontSize:12}}>
              <div>
                <span style={{color:T.amber,fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{o.orderNumber}</span>
                <span style={{color:T.muted,marginLeft:8}}>{o.customerName||'—'}</span>
              </div>
              <div style={{display:'flex',gap:7,alignItems:'center'}}>
                <Pill label={o.status} color={STATUS_COLOR[o.status]}/>
                <span style={{color:T.green,fontWeight:600}}>{fmtAmt(o.total)}</span>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: Users
   ════════════════════════════════════════════════════════════════════════════ */
function UsersTab({ isSuperAdmin }) {
  const [users, setUsers]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [q, setQ]           = useState('');
  const [role, setRole]     = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage]     = useState(1);
  const [sel, setSel]       = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);
  const [tkModal, setTkModal] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams({page,limit:30});
    if (q)      p.set('q',q);
    if (role)   p.set('role',role);
    if (status) p.set('status',status);
    // Superadmin uses the full-access endpoint
    const endpoint = isSuperAdmin ? `/users/superadmin/all?${p}` : `/admin/users?${p}`;
    const {data} = await api.get(endpoint);
    setUsers(data.users||[]); setTotal(data.total||0);
  }, [q,role,status,page,isSuperAdmin]);

  useEffect(()=>{ load(); },[load]);

  const openDetail = async u => {
    setSel(u);
    setForm({
      username:u.username, email:u.email, role:u.role, status:u.status,
      plan:u.plan, primaryFlapid:u.primaryFlapid||'', phone_number_1:u.phone_number_1||'',
      organization:u.organization||'', jobtitle:u.jobtitle||'', address:u.address||'',
    });
    // Load full detail (superadmin gets more)
    const endpoint = isSuperAdmin?`/users/superadmin/${u._id}`:`/admin/users/${u._id}`;
    const {data} = await api.get(endpoint);
    setDetail(data.user||data);
  };

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = isSuperAdmin?`/users/superadmin/${sel._id}`:`/admin/users/${sel._id}`;
      await api.put(endpoint, form);
      setSel(null); setDetail(null); load();
    } catch(e) { alert(e.response?.data?.error||'Save failed'); }
    setSaving(false);
  };

  const cols = [
    {key:'username',    label:'Name',    render:(v,r)=><span style={{fontWeight:700,color:T.text}}>{v||r.email}</span>},
    {key:'email',       label:'Email',   render:v=><span style={{color:T.muted,fontSize:12}}>{v}</span>},
    {key:'primaryFlapid',label:'Flap ID',render:v=><span style={{color:T.amber,fontFamily:'JetBrains Mono,monospace',fontSize:12}}>{v||'—'}</span>},
    {key:'role',        label:'Role',    render:v=><Pill label={v||'user'} color={v==='superadmin'?T.amber:v==='admin'?T.purple:T.muted}/>},
    {key:'plan',        label:'Plan',    render:v=><Pill label={v||'free'} color={STATUS_COLOR[v]||T.muted}/>},
    {key:'status',      label:'Status',  render:v=><Pill label={v||'active'} color={STATUS_COLOR[v]||T.muted}/>},
    {key:'createdAt',   label:'Joined',  render:v=><span style={{color:T.muted,fontSize:12}}>{fmtDate(v)}</span>},
  ];

  return (
    <div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14}}>
        <input value={q} onChange={e=>{setQ(e.target.value);setPage(1);}}
          placeholder="Search name / email / flapid…"
          style={{flex:1,minWidth:180,background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
            borderRadius:9,padding:'9px 12px',color:T.text,fontSize:13,outline:'none'}}/>
        {[['role','All Roles',['','user','admin','superadmin']],
          ['status','All Status',['','active','suspended','banned']]
        ].map(([k,ph,opts])=>(
          <select key={k} value={k==='role'?role:status}
            onChange={e=>{k==='role'?setRole(e.target.value):setStatus(e.target.value);setPage(1);}}
            style={{background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
              borderRadius:9,padding:'9px 10px',color:T.text,fontSize:13,outline:'none'}}>
            {opts.map(o=><option key={o} value={o}>{o||ph}</option>)}
          </select>
        ))}
        <span style={{fontSize:12,color:T.muted,alignSelf:'center'}}>{total} users</span>
      </div>
      <Table cols={cols} rows={users} onRowClick={openDetail}/>
      <div style={{display:'flex',justifyContent:'center',gap:6,marginTop:12}}>
        {[...Array(Math.min(10,Math.ceil(total/30)))].map((_,i)=>(
          <button key={i} onClick={()=>setPage(i+1)}
            style={{padding:'5px 11px',borderRadius:7,border:'none',cursor:'pointer',fontSize:12,
              background:page===i+1?T.amber:`rgba(${rgb(T.amber)},.08)`,
              color:page===i+1?'#fff':T.muted,fontWeight:page===i+1?700:400}}>
            {i+1}
          </button>
        ))}
      </div>

      {sel&&(
        <Modal title={`User: ${sel.username||sel.email}`} onClose={()=>{setSel(null);setDetail(null);}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Field label="Username"   value={form.username}     onChange={v=>setForm(p=>({...p,username:v}))}/>
            <Field label="Email"      value={form.email}        onChange={v=>setForm(p=>({...p,email:v}))}/>
            <Field label="Primary Flap ID" value={form.primaryFlapid} onChange={v=>setForm(p=>({...p,primaryFlapid:v}))}
              placeholder="e.g. bikalpa"/>
            <Field label="Phone"      value={form.phone_number_1} onChange={v=>setForm(p=>({...p,phone_number_1:v}))} type="tel"/>
            <Field label="Plan"       value={form.plan}         onChange={v=>setForm(p=>({...p,plan:v}))}
              options={['free','pro','business']}/>
            <Field label="Status"     value={form.status}       onChange={v=>setForm(p=>({...p,status:v}))}
              options={['active','suspended','banned']}/>
            {isSuperAdmin&&(
              <Field label="Role (superadmin only)" value={form.role} onChange={v=>setForm(p=>({...p,role:v}))}
                options={['user','admin','superadmin']}/>
            )}
            <Field label="Organization" value={form.organization} onChange={v=>setForm(p=>({...p,organization:v}))}/>
            <Field label="Job Title"  value={form.jobtitle}     onChange={v=>setForm(p=>({...p,jobtitle:v}))}/>
            <div style={{gridColumn:'1/-1'}}>
              <Field label="Address"  value={form.address}      onChange={v=>setForm(p=>({...p,address:v}))}/>
            </div>
          </div>
          {detail&&(
            <div style={{marginTop:10,padding:'12px 14px',background:'rgba(0,0,0,0.03)',
              borderRadius:10,border:`1px solid ${T.dim}`,fontSize:12,color:T.muted}}>
              <div style={{marginBottom:6}}>
                Accessories: <strong style={{color:T.cyan}}>{detail.accessories?.length||0}</strong>
                {' · '}Emergency contacts: <strong style={{color:T.amber}}>{detail.emergencyContacts?.length||0}</strong>
                {' · '}FlapID: <strong style={{color:T.teal,fontFamily:'JetBrains Mono,monospace'}}>{detail.primaryFlapid||'not set'}</strong>
              </div>
              {detail.accessories?.length>0&&(
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {detail.accessories.map((a,i)=>(
                    <span key={i} style={{fontSize:10,padding:'2px 8px',borderRadius:99,
                      background:`rgba(${rgb(T.cyan)},.08)`,color:T.cyan,border:`1px solid rgba(${rgb(T.cyan)},.15)`,
                      fontFamily:'JetBrains Mono,monospace'}}>
                      {a.flapid||a.uid||'—'} ({a.type}){a.isLost?' 🔴':''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <Btn outline color={T.purple} onClick={()=>setTkModal(sel)}>🪙 Tokens</Btn>
            <Btn onClick={save} disabled={saving} style={{flex:1}}>
              {saving?'Saving…':'Save Changes'}
            </Btn>
          </div>
        </Modal>
      )}

      {tkModal&&<TokenModal user={tkModal} onClose={()=>setTkModal(null)}
        onSubmit={async f=>{await api.post('/admin/flapcare/issue',{userId:tkModal._id,...f});setTkModal(null);}}/>}
    </div>
  );
}

function TokenModal({ user, onClose, onSubmit }) {
  const [form, setForm] = useState({type:'credit',amount:0,reason:''});
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`FlapCare Tokens — ${user.username||user.email}`} onClose={onClose} width={400}>
      <Field label="Type"   value={form.type}   onChange={v=>setForm(p=>({...p,type:v}))}
        options={['credit','debit','adjustment','refund']}/>
      <Field label="Amount" value={form.amount} onChange={v=>setForm(p=>({...p,amount:Number(v)}))} type="number"/>
      <Field label="Reason" value={form.reason} onChange={v=>setForm(p=>({...p,reason:v}))} placeholder="Reason for transaction"/>
      <Btn onClick={async()=>{setBusy(true);await onSubmit(form);setBusy(false);}}
        disabled={busy||!form.amount} style={{width:'100%',marginTop:8}}>
        {busy?'Issuing…':'Issue Tokens'}
      </Btn>
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: Products
   ════════════════════════════════════════════════════════════════════════════ */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [modal, setModal]   = useState(null);
  const [invOf, setInvOf]   = useState(null);
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);

  const load = ()=>api.get('/admin/products').then(r=>setProducts(r.data.products||[]));
  useEffect(()=>{load();},[]);

  const save = async () => {
    setSaving(true);
    if(modal==='new') await api.post('/admin/products',form);
    else await api.put(`/admin/products/${modal._id}`,form);
    setSaving(false); setModal(null); load();
  };

  const cols = [
    {key:'name',       label:'Product',  render:v=><span style={{fontWeight:700,color:T.text}}>{v}</span>},
    {key:'type',       label:'Type',     render:v=><Pill label={v} color={T.purple}/>},
    {key:'basePrice',  label:'Price',    render:v=><span style={{color:T.green,fontWeight:600}}>{fmtAmt(v)}</span>},
    {key:'stockCounts',label:'Stock',    render:v=>v?(<div style={{display:'flex',gap:6,fontSize:11}}>
      <span style={{color:T.green}}>{v.unassigned} avail</span>
      {v.assigned>0&&<span style={{color:T.amber}}>{v.assigned} out</span>}
      {v.defective>0&&<span style={{color:T.red}}>{v.defective} def</span>}
    </div>):'—'},
    {key:'isActive',   label:'Active',   render:v=><Pill label={v?'Yes':'No'} color={v?T.green:T.red}/>},
    {key:'_id',        label:'Actions',  render:(_,r)=>(
      <div style={{display:'flex',gap:5}}>
        <button onClick={e=>{e.stopPropagation();setInvOf(r);}}
          style={{padding:'3px 10px',borderRadius:6,border:`1px solid rgba(${rgb(T.amber)},.3)`,
            background:`rgba(${rgb(T.amber)},.06)`,color:T.amber,cursor:'pointer',fontSize:11,fontWeight:700}}>Units</button>
        <button onClick={e=>{e.stopPropagation();setForm({...r});setModal(r);}}
          style={{padding:'3px 10px',borderRadius:6,border:`1px solid ${T.dim}`,
            background:'rgba(0,0,0,0.03)',color:T.muted,cursor:'pointer',fontSize:11}}>Edit</button>
      </div>
    )},
  ];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
        <Btn onClick={()=>{setForm({name:'',type:'card',description:'',basePrice:0,isActive:true,isFeatured:false});setModal('new');}}>
          + New Product
        </Btn>
      </div>
      <Table cols={cols} rows={products} onRowClick={p=>setInvOf(p)}/>
      {invOf&&<UnitInventory product={invOf} onClose={()=>{setInvOf(null);load();}}/>}
      {modal&&(
        <Modal title={modal==='new'?'New Product':`Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div style={{gridColumn:'1/-1'}}><Field label="Product Name" value={form.name} onChange={v=>setForm(p=>({...p,name:v}))}/></div>
            <Field label="Type" value={form.type} onChange={v=>setForm(p=>({...p,type:v}))}
              options={['card','wallet','keyring','ring','stand','other']}/>
            <Field label="Base Price (cents)" value={form.basePrice} onChange={v=>setForm(p=>({...p,basePrice:Number(v)}))} type="number"/>
            <div style={{gridColumn:'1/-1'}}><Field label="Description" value={form.description} onChange={v=>setForm(p=>({...p,description:v}))} rows={3}/></div>
            <Field label="Active"   value={form.isActive?'true':'false'}   onChange={v=>setForm(p=>({...p,isActive:v==='true'}))}   options={['true','false']}/>
            <Field label="Featured" value={form.isFeatured?'true':'false'} onChange={v=>setForm(p=>({...p,isFeatured:v==='true'}))} options={['true','false']}/>
          </div>
          <Btn onClick={save} disabled={saving} style={{width:'100%',marginTop:12}}>{saving?'Saving…':'Save Product'}</Btn>
        </Modal>
      )}
    </div>
  );
}

function UnitInventory({ product, onClose }) {
  const [units, setUnits]   = useState([]);
  const [sf, setSf]         = useState('');
  const [q, setQ]           = useState('');
  const [mode, setMode]     = useState(null); // 'single' | 'bulk'
  const [af, setAf]         = useState({flapid:'',uid:'',variantName:'',batchCode:''});
  const [bulk, setBulk]     = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [lu, setLu]         = useState('');
  const [luRes, setLuRes]   = useState(null);

  const load = useCallback(async()=>{
    const p=new URLSearchParams();
    if(sf)p.set('status',sf);if(q)p.set('q',q);
    const {data}=await api.get(`/admin/products/${product._id}/units?${p}`);
    setUnits(data.units||[]);
  },[product._id,sf,q]);
  useEffect(()=>{load();},[load]);

  const addSingle=async()=>{
    if(!af.flapid||!af.uid){setErr('Flap ID and UID required');return;}
    setSaving(true);setErr('');
    try{await api.post(`/admin/products/${product._id}/units`,af);setMode(null);setAf({flapid:'',uid:'',variantName:'',batchCode:''});load();}
    catch(e){setErr(e.response?.data?.error||'Error');}
    setSaving(false);
  };
  const addBulk=async()=>{
    const rows=bulk.trim().split('\n').filter(Boolean)
      .map(l=>{const[flapid,uid,variantName,batchCode]=l.split(',').map(s=>s.trim());return{flapid,uid,variantName,batchCode};});
    setSaving(true);setErr('');
    try{await api.post(`/admin/products/${product._id}/units/bulk`,{units:rows});setMode(null);setBulk('');load();}
    catch(e){setErr(e.response?.data?.error||'Bulk failed');}
    setSaving(false);
  };
  const mark=async(id,status)=>{await api.patch(`/admin/products/${product._id}/units/${id}`,{status});load();};
  const lookup=async()=>{
    try{const isUid=lu.includes(':');const p=isUid?`uid=${lu}`:`flapid=${lu}`;
      const{data}=await api.get(`/admin/inventory/lookup?${p}`);setLuRes(data);}
    catch(e){setLuRes({error:e.response?.data?.error||'Not found'});}
  };
  const SC={unassigned:T.green,reserved:T.teal,assigned:T.amber,defective:T.red,returned:T.muted};
  const counts={};units.forEach(u=>{counts[u.status]=(counts[u.status]||0)+1;});

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1000,
      display:'flex',alignItems:'flex-start',justifyContent:'center',
      backdropFilter:'blur(6px)',padding:20,overflowY:'auto'}}>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,
        width:'min(960px,95vw)',boxShadow:'0 24px 64px rgba(0,0,0,0.12)'}}>
        {/* Header */}
        <div style={{padding:'18px 22px',borderBottom:`1px solid ${T.dim}`,
          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:T.text,fontFamily:'Syne,sans-serif'}}>
              {product.name} — Unit Inventory
            </div>
            <div style={{display:'flex',gap:12,marginTop:6}}>
              {Object.entries(SC).map(([s,c])=>(
                <span key={s} style={{fontSize:11,color:c,fontFamily:'JetBrains Mono,monospace'}}>
                  <strong>{counts[s]||0}</strong> {s}
                </span>
              ))}
            </div>
          </div>
          <div style={{display:'flex',gap:7}}>
            <Btn onClick={()=>setMode('single')}>+ Add Unit</Btn>
            <Btn outline onClick={()=>setMode('bulk')}>⬆ Bulk</Btn>
            <button onClick={onClose} style={{width:32,height:32,borderRadius:8,
              border:`1px solid ${T.dim}`,background:'rgba(0,0,0,0.03)',color:T.muted,cursor:'pointer',fontSize:14,
              display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
          </div>
        </div>
        {/* Lookup */}
        <div style={{padding:'12px 22px',borderBottom:`1px solid ${T.dim}`,
          display:'flex',gap:8,alignItems:'center',background:`rgba(${rgb(T.amber)},.03)`}}>
          <span style={{fontSize:12,color:T.amber,fontWeight:700}}>🔍 Lookup:</span>
          <input value={lu} onChange={e=>setLu(e.target.value)} onKeyDown={e=>e.key==='Enter'&&lookup()}
            placeholder="Enter Flap ID or NFC UID…"
            style={{flex:1,background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
              borderRadius:8,padding:'7px 11px',color:T.text,fontSize:13,outline:'none',
              fontFamily:'JetBrains Mono,monospace'}}/>
          <Btn onClick={lookup}>Search</Btn>
          {luRes&&<div style={{fontSize:12,color:luRes.error?T.red:T.green,marginLeft:8}}>
            {luRes.error||`✓ ${luRes.product?.name} — ${luRes.unit?.status}${luRes.owner?' → '+luRes.owner.username:''}`}
          </div>}
        </div>
        {/* Filters */}
        <div style={{padding:'10px 22px',display:'flex',gap:8}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter by Flap ID / UID…"
            style={{flex:1,background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
              borderRadius:8,padding:'7px 11px',color:T.text,fontSize:13,outline:'none'}}/>
          <select value={sf} onChange={e=>setSf(e.target.value)}
            style={{background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
              borderRadius:8,padding:'7px 10px',color:T.text,fontSize:13,outline:'none'}}>
            {['','unassigned','reserved','assigned','defective','returned'].map(s=>(
              <option key={s} value={s}>{s||'All Statuses'}</option>
            ))}
          </select>
          <span style={{fontSize:12,color:T.muted,alignSelf:'center'}}>{units.length} units</span>
        </div>
        {/* Units table */}
        <div style={{padding:'0 22px 20px',maxHeight:360,overflowY:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'rgba(0,0,0,0.02)'}}>
                {['Flap ID','NFC UID','Variant','Batch','Status','Actions'].map(h=>(
                  <th key={h} style={{padding:'8px 10px',textAlign:'left',fontSize:10,fontWeight:700,
                    textTransform:'uppercase',letterSpacing:1,color:T.muted,
                    borderBottom:`1px solid ${T.dim}`,fontFamily:'JetBrains Mono,monospace'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {units.length===0&&<tr><td colSpan={6} style={{textAlign:'center',padding:'32px 0',color:T.muted}}>No units yet</td></tr>}
              {units.map(u=>(
                <tr key={u._id} style={{borderBottom:`1px solid rgba(0,0,0,0.04)`}}>
                  <td style={{padding:'9px 10px',fontFamily:'JetBrains Mono,monospace',color:T.amber,fontWeight:700,fontSize:12}}>{u.flapid}</td>
                  <td style={{padding:'9px 10px',fontFamily:'JetBrains Mono,monospace',color:T.muted,fontSize:11}}>{u.uid}</td>
                  <td style={{padding:'9px 10px',color:T.text}}>{u.variantName||'—'}</td>
                  <td style={{padding:'9px 10px',color:T.muted}}>{u.batchCode||'—'}</td>
                  <td style={{padding:'9px 10px'}}><Pill label={u.status} color={SC[u.status]||T.muted}/></td>
                  <td style={{padding:'9px 10px'}}>
                    {u.status!=='defective'
                      ?<button onClick={()=>mark(u._id,'defective')}
                          style={{padding:'3px 8px',borderRadius:5,border:`1px solid rgba(${rgb(T.red)},.3)`,
                            background:`rgba(${rgb(T.red)},.06)`,color:T.red,cursor:'pointer',fontSize:10}}>Defect</button>
                      :<button onClick={()=>mark(u._id,'unassigned')}
                          style={{padding:'3px 8px',borderRadius:5,border:`1px solid rgba(${rgb(T.green)},.3)`,
                            background:`rgba(${rgb(T.green)},.06)`,color:T.green,cursor:'pointer',fontSize:10}}>Restore</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Add forms */}
        {mode&&(
          <div style={{padding:'0 22px 22px'}}>
            <Card style={{background:'rgba(0,0,0,0.02)'}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12,fontFamily:'Syne,sans-serif'}}>
                {mode==='single'?'Add Physical Unit':'Bulk Import'}
              </div>
              {err&&<div style={{color:T.red,fontSize:12,marginBottom:8}}>{err}</div>}
              {mode==='single'?(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <Field label="Flap ID *"  value={af.flapid}      onChange={v=>setAf(p=>({...p,flapid:v}))}/>
                  <Field label="NFC UID *"  value={af.uid}         onChange={v=>setAf(p=>({...p,uid:v}))}/>
                  <Field label="Variant"    value={af.variantName} onChange={v=>setAf(p=>({...p,variantName:v}))}/>
                  <Field label="Batch Code" value={af.batchCode}   onChange={v=>setAf(p=>({...p,batchCode:v}))}/>
                </div>
              ):(
                <div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:8}}>
                    One per line: <code style={{color:T.amber,fontFamily:'JetBrains Mono,monospace'}}>flapid,uid,variantName,batchCode</code>
                  </div>
                  <textarea value={bulk} onChange={e=>setBulk(e.target.value)} rows={6}
                    style={{width:'100%',background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
                      borderRadius:9,padding:'9px 12px',color:T.text,fontSize:12,outline:'none',
                      resize:'vertical',boxSizing:'border-box',fontFamily:'JetBrains Mono,monospace',lineHeight:1.6}}/>
                </div>
              )}
              <div style={{display:'flex',gap:8,marginTop:10}}>
                <Btn outline onClick={()=>{setMode(null);setErr('');}}>Cancel</Btn>
                <Btn onClick={mode==='single'?addSingle:addBulk} disabled={saving} style={{flex:1}}>
                  {saving?'Adding…':mode==='single'?'Add Unit':`Import ${bulk.trim().split('\n').filter(Boolean).length} Units`}
                </Btn>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: Orders
   ════════════════════════════════════════════════════════════════════════════ */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal]   = useState(0);
  const [q, setQ]           = useState('');
  const [sf, setSf]         = useState('');
  const [page, setPage]     = useState(1);
  const [sel, setSel]       = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);

  const load=useCallback(async()=>{
    const p=new URLSearchParams({page});
    if(q)p.set('q',q);if(sf)p.set('status',sf);
    const{data}=await api.get(`/admin/orders?${p}`);
    setOrders(data.orders||[]);setTotal(data.total||0);
  },[q,sf,page]);
  useEffect(()=>{load();},[load]);

  const open=async o=>{
    setSel(o);setForm({status:o.status,paymentStatus:o.paymentStatus,
      trackingNumber:o.trackingNumber||'',trackingCarrier:o.trackingCarrier||'',adminNotes:o.adminNotes||''});
    const{data}=await api.get(`/admin/orders/${o._id}`);setDetail(data.order);
  };
  const save=async()=>{setSaving(true);await api.put(`/admin/orders/${sel._id}`,form);setSaving(false);setSel(null);load();};

  const cols=[
    {key:'orderNumber',  label:'Order #', render:v=><span style={{color:T.amber,fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{v}</span>},
    {key:'customerName', label:'Customer',render:(v,r)=><span style={{color:T.text}}>{v||r.customerEmail}</span>},
    {key:'status',       label:'Status',  render:v=><Pill label={v} color={STATUS_COLOR[v]}/>},
    {key:'paymentStatus',label:'Payment', render:v=><Pill label={v} color={v==='paid'?T.green:v==='refunded'?T.muted:T.red}/>},
    {key:'total',        label:'Total',   render:v=><span style={{color:T.green,fontWeight:600}}>{fmtAmt(v)}</span>},
    {key:'createdAt',    label:'Date',    render:v=><span style={{color:T.muted,fontSize:12}}>{fmtDate(v)}</span>},
  ];

  return (
    <div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14}}>
        <input value={q} onChange={e=>{setQ(e.target.value);setPage(1);}} placeholder="Search order / customer…"
          style={{flex:1,minWidth:180,background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
            borderRadius:9,padding:'9px 12px',color:T.text,fontSize:13,outline:'none'}}/>
        <select value={sf} onChange={e=>{setSf(e.target.value);setPage(1);}}
          style={{background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
            borderRadius:9,padding:'9px 10px',color:T.text,fontSize:13,outline:'none'}}>
          {['','pending','confirmed','processing','shipped','delivered','cancelled','refunded'].map(s=>(
            <option key={s} value={s}>{s||'All Statuses'}</option>
          ))}
        </select>
        <span style={{fontSize:12,color:T.muted,alignSelf:'center'}}>{total} orders</span>
      </div>
      <Table cols={cols} rows={orders} onRowClick={open}/>

      {sel&&(
        <Modal title={`Order ${sel.orderNumber}`} onClose={()=>{setSel(null);setDetail(null);}} width={640}>
          {detail&&<div style={{marginBottom:16}}>
            <div style={{fontSize:12,color:T.muted,marginBottom:8}}>
              <strong style={{color:T.text}}>{detail.customerName}</strong> · {detail.customerEmail}
            </div>
            {detail.items?.map((it,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',
                padding:'7px 10px',background:'rgba(0,0,0,0.03)',borderRadius:8,marginBottom:5}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:T.text}}>{it.productName}</div>
                  <div style={{fontSize:11,color:T.muted}}>qty {it.quantity}</div>
                </div>
                <div style={{fontSize:13,color:T.green,fontWeight:600}}>{fmtAmt(it.totalPrice)}</div>
              </div>
            ))}
            <div style={{textAlign:'right',fontSize:14,fontWeight:700,color:T.text,marginTop:6}}>
              Total: {fmtAmt(detail.total)}
            </div>
            {detail.timeline?.slice(-3).map((t,i)=>(
              <div key={i} style={{display:'flex',gap:8,fontSize:11,marginTop:5}}>
                <Pill label={t.status} color={STATUS_COLOR[t.status]}/>
                <span style={{color:T.muted}}>{t.note}</span>
                <span style={{color:T.dim,marginLeft:'auto'}}>{fmtDT(t.at)}</span>
              </div>
            ))}
          </div>}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Field label="Status"          value={form.status}         onChange={v=>setForm(p=>({...p,status:v}))}
              options={['pending','confirmed','processing','shipped','delivered','cancelled','refunded']}/>
            <Field label="Payment Status"  value={form.paymentStatus}  onChange={v=>setForm(p=>({...p,paymentStatus:v}))}
              options={['unpaid','paid','refunded']}/>
            <Field label="Tracking Number" value={form.trackingNumber} onChange={v=>setForm(p=>({...p,trackingNumber:v}))}/>
            <Field label="Carrier"         value={form.trackingCarrier} onChange={v=>setForm(p=>({...p,trackingCarrier:v}))}/>
            <div style={{gridColumn:'1/-1'}}><Field label="Admin Notes" value={form.adminNotes} onChange={v=>setForm(p=>({...p,adminNotes:v}))} rows={2}/></div>
          </div>
          <Btn onClick={save} disabled={saving} style={{width:'100%',marginTop:8}}>{saving?'Saving…':'Update Order'}</Btn>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: Support
   ════════════════════════════════════════════════════════════════════════════ */
function SupportTab() {
  const [tickets,setTickets]=useState([]);
  const [q,setQ]=useState('');const[sf,setSf]=useState('open');const[pf,setPf]=useState('');
  const [sel,setSel]=useState(null);const[detail,setDetail]=useState(null);
  const [reply,setReply]=useState('');const[form,setForm]=useState({});const[saving,setSaving]=useState(false);

  const load=useCallback(async()=>{
    const p=new URLSearchParams();if(q)p.set('q',q);if(sf)p.set('status',sf);if(pf)p.set('priority',pf);
    const{data}=await api.get(`/admin/tickets?${p}`);setTickets(data.tickets||[]);
  },[q,sf,pf]);
  useEffect(()=>{load();},[load]);

  const open=async t=>{
    setSel(t);setForm({status:t.status,priority:t.priority});
    const{data}=await api.get(`/admin/tickets/${t._id}`);setDetail(data.ticket);
  };
  const sendReply=async()=>{
    if(!reply.trim())return;
    await api.post(`/admin/tickets/${sel._id}/reply`,{message:reply});
    setReply('');const{data}=await api.get(`/admin/tickets/${sel._id}`);setDetail(data.ticket);
  };
  const save=async()=>{setSaving(true);await api.put(`/admin/tickets/${sel._id}`,form);setSaving(false);load();};

  const PC={urgent:T.red,high:T.amber,medium:T.teal,low:T.muted};
  const cols=[
    {key:'ticketNumber',label:'Ticket #',  render:v=><span style={{color:T.purple,fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{v}</span>},
    {key:'subject',     label:'Subject',   render:v=><span style={{color:T.text}}>{v}</span>},
    {key:'userEmail',   label:'User',      render:v=><span style={{color:T.muted,fontSize:12}}>{v}</span>},
    {key:'priority',    label:'Priority',  render:v=><Pill label={v} color={PC[v]||T.muted}/>},
    {key:'status',      label:'Status',    render:v=><Pill label={v} color={STATUS_COLOR[v]}/>},
    {key:'createdAt',   label:'Created',   render:v=><span style={{color:T.muted,fontSize:12}}>{fmtDate(v)}</span>},
  ];

  return (
    <div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search ticket / email…"
          style={{flex:1,minWidth:180,background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
            borderRadius:9,padding:'9px 12px',color:T.text,fontSize:13,outline:'none'}}/>
        {[['sf',sf,setSf,['','open','in_progress','waiting','resolved','closed'],'Status'],
          ['pf',pf,setPf,['','urgent','high','medium','low'],'Priority']].map(([k,v,set,opts,ph])=>(
          <select key={k} value={v} onChange={e=>set(e.target.value)}
            style={{background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
              borderRadius:9,padding:'9px 10px',color:T.text,fontSize:13,outline:'none'}}>
            {opts.map(o=><option key={o} value={o}>{o||`All ${ph}`}</option>)}
          </select>
        ))}
      </div>
      <Table cols={cols} rows={tickets} onRowClick={open}/>
      {sel&&(
        <Modal title={`${sel.ticketNumber} — ${sel.subject}`} onClose={()=>{setSel(null);setDetail(null);}} width={680}>
          <div style={{display:'flex',gap:10,marginBottom:14}}>
            <Field label="Status" value={form.status} onChange={v=>setForm(p=>({...p,status:v}))}
              options={['open','in_progress','waiting','resolved','closed']}/>
            <Field label="Priority" value={form.priority} onChange={v=>setForm(p=>({...p,priority:v}))}
              options={['low','medium','high','urgent']}/>
            <Btn onClick={save} disabled={saving} style={{alignSelf:'flex-end',whiteSpace:'nowrap'}}>
              {saving?'…':'Update'}
            </Btn>
          </div>
          <div style={{maxHeight:280,overflowY:'auto',marginBottom:14,display:'flex',flexDirection:'column',gap:8}}>
            {detail?(
              <>
                <div style={{padding:'11px 14px',background:`rgba(${rgb(T.teal)},.05)`,
                  borderRadius:10,borderLeft:`3px solid ${T.teal}`}}>
                  <div style={{fontSize:11,color:T.muted,marginBottom:4}}>{detail.userEmail} · {fmtDT(detail.createdAt)}</div>
                  <div style={{fontSize:13,color:T.text,lineHeight:1.6}}>{detail.description}</div>
                </div>
                {(detail.replies||[]).map((r,i)=>(
                  <div key={i} style={{padding:'11px 14px',borderRadius:10,
                    background:r.isStaff?`rgba(${rgb(T.amber)},.05)`:'rgba(0,0,0,0.03)',
                    borderLeft:`3px solid ${r.isStaff?T.amber:T.muted}`}}>
                    <div style={{fontSize:11,color:T.muted,marginBottom:4}}>
                      {r.isStaff?'🛡 '+r.authorName:r.authorName} · {fmtDT(r.at)}
                    </div>
                    <div style={{fontSize:13,color:T.text,lineHeight:1.6}}>{r.message}</div>
                  </div>
                ))}
              </>
            ):<div style={{color:T.muted,fontSize:13}}>Loading…</div>}
          </div>
          <textarea value={reply} onChange={e=>setReply(e.target.value)} rows={3}
            placeholder="Write a reply…"
            style={{width:'100%',background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
              borderRadius:9,padding:'9px 12px',color:T.text,fontSize:13,outline:'none',
              resize:'vertical',boxSizing:'border-box',fontFamily:'DM Sans,sans-serif',marginBottom:8}}/>
          <Btn onClick={sendReply}>Send Reply ➤</Btn>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: FlapCare
   ════════════════════════════════════════════════════════════════════════════ */
function FlapCareTab() {
  const [plans,setPlans]=useState([]);const[wallets,setWallets]=useState([]);
  const [pm,setPm]=useState(null);const[pf,setPf]=useState({});const[saving,setSaving]=useState(false);
  const load=()=>{api.get('/admin/flapcare/plans').then(r=>setPlans(r.data.plans||[]));
    api.get('/admin/flapcare/wallets').then(r=>setWallets(r.data.wallets||[]));};
  useEffect(()=>{load();},[]);
  const save=async()=>{setSaving(true);
    if(pm==='new')await api.post('/admin/flapcare/plans',pf);
    else await api.put(`/admin/flapcare/plans/${pm._id}`,pf);
    setSaving(false);setPm(null);load();};
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:700,color:T.text,fontFamily:'Syne,sans-serif'}}>🪙 FlapCare Plans</div>
          <Btn onClick={()=>{setPf({name:'',tokens:0,price:0,description:'',isActive:true});setPm('new');}}>+ Plan</Btn>
        </div>
        {plans.map(p=>(
          <Card key={p._id} style={{marginBottom:10}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:700,color:T.text,marginBottom:3,fontFamily:'Syne,sans-serif'}}>{p.name}</div>
                <div style={{fontSize:12,color:T.muted}}>{p.description}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:18,fontWeight:800,color:T.amber,fontFamily:'Syne,sans-serif'}}>{p.tokens} <span style={{fontSize:11,fontWeight:400}}>tokens</span></div>
                <div style={{fontSize:13,color:T.green}}>{fmtAmt(p.price)}</div>
              </div>
            </div>
            <div style={{marginTop:10,display:'flex',gap:7,alignItems:'center'}}>
              <Pill label={p.isActive?'Active':'Inactive'} color={p.isActive?T.green:T.muted}/>
              <Btn outline onClick={()=>{setPf({...p});setPm(p);}} style={{fontSize:11,padding:'3px 10px'}}>Edit</Btn>
            </div>
          </Card>
        ))}
      </div>
      <div>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:14,fontFamily:'Syne,sans-serif'}}>💰 Top Wallets</div>
        {wallets.slice(0,15).map((w,i)=>(
          <div key={w._id} style={{display:'flex',alignItems:'center',gap:10,
            padding:'9px 0',borderBottom:`1px solid ${T.dim}`}}>
            <div style={{width:20,fontSize:12,color:T.muted,textAlign:'center'}}>{i+1}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text}}>{w.userId?.username||w.userId?.email||'—'}</div>
              <div style={{fontSize:11,color:T.muted,fontFamily:'JetBrains Mono,monospace'}}>{w.userId?.primaryFlapid||''}</div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:15,fontWeight:800,color:T.amber}}>{w.balance}</div>
              <div style={{fontSize:10,color:T.muted}}>lifetime: {w.lifetime}</div>
            </div>
          </div>
        ))}
      </div>
      {pm&&(
        <Modal title={pm==='new'?'New FlapCare Plan':`Edit: ${pm.name}`} onClose={()=>setPm(null)} width={420}>
          <Field label="Plan Name"      value={pf.name}        onChange={v=>setPf(p=>({...p,name:v}))}/>
          <Field label="Tokens"         value={pf.tokens}      onChange={v=>setPf(p=>({...p,tokens:Number(v)}))} type="number"/>
          <Field label="Price (cents)"  value={pf.price}       onChange={v=>setPf(p=>({...p,price:Number(v)}))} type="number"/>
          <Field label="Description"    value={pf.description} onChange={v=>setPf(p=>({...p,description:v}))} rows={2}/>
          <Field label="Active"         value={pf.isActive?'true':'false'} onChange={v=>setPf(p=>({...p,isActive:v==='true'}))} options={['true','false']}/>
          <Btn onClick={save} disabled={saving} style={{width:'100%',marginTop:8}}>{saving?'Saving…':'Save Plan'}</Btn>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB: Accessories
   ════════════════════════════════════════════════════════════════════════════ */
function AccessoriesTab() {
  const [items,setItems]=useState([]);const[q,setQ]=useState('');
  const [type,setType]=useState('');const[lostOnly,setLostOnly]=useState(false);
  const load=useCallback(async()=>{
    const p=new URLSearchParams();if(q)p.set('q',q);if(type)p.set('type',type);if(lostOnly)p.set('isLost','true');
    const{data}=await api.get(`/admin/accessories?${p}`);setItems(data.accessories||[]);
  },[q,type,lostOnly]);
  useEffect(()=>{load();},[load]);
  const cols=[
    {key:'flapid',        label:'Flap ID', render:v=><span style={{color:T.amber,fontFamily:'JetBrains Mono,monospace',fontSize:12}}>{v||'—'}</span>},
    {key:'uid',           label:'UID',     render:v=><span style={{color:T.muted,fontFamily:'JetBrains Mono,monospace',fontSize:11}}>{v||'—'}</span>},
    {key:'type',          label:'Type',    render:v=><Pill label={v} color={T.purple}/>},
    {key:'ownerUsername', label:'Owner',   render:v=><span style={{color:T.text}}>{v||'—'}</span>},
    {key:'ownerEmail',    label:'Email',   render:v=><span style={{color:T.muted,fontSize:12}}>{v}</span>},
    {key:'isPrimary',     label:'Primary', render:v=>v?<span style={{color:T.green}}>✓</span>:'—'},
    {key:'isLost',        label:'Lost',    render:v=>v?<span style={{color:T.red}}>⚠ Lost</span>:'—'},
    {key:'addedAt',       label:'Added',   render:v=><span style={{color:T.muted,fontSize:11}}>{fmtDate(v)}</span>},
  ];
  return (
    <div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search flapid / uid / owner…"
          style={{flex:1,minWidth:180,background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
            borderRadius:9,padding:'9px 12px',color:T.text,fontSize:13,outline:'none'}}/>
        <select value={type} onChange={e=>setType(e.target.value)}
          style={{background:'rgba(0,0,0,0.03)',border:`1px solid ${T.dim}`,
            borderRadius:9,padding:'9px 10px',color:T.text,fontSize:13,outline:'none'}}>
          {['','card','wallet','keyring','ring','other'].map(t=><option key={t} value={t}>{t||'All Types'}</option>)}
        </select>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,color:T.muted,cursor:'pointer'}}>
          <input type="checkbox" checked={lostOnly} onChange={e=>setLostOnly(e.target.checked)}
            style={{accentColor:T.red}}/>
          Lost only
        </label>
        <span style={{fontSize:12,color:T.muted,alignSelf:'center'}}>{items.length}</span>
      </div>
      <Table cols={cols} rows={items}/>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   MAIN SHELL
   ════════════════════════════════════════════════════════════════════════════ */
const SA_TABS = [
  {key:'overview',   icon:'📊', label:'Overview',    color:T.amber},
  {key:'users',      icon:'👥', label:'Users',       color:T.cyan},
  {key:'products',   icon:'🛍️', label:'Products',   color:T.purple},
  {key:'orders',     icon:'📦', label:'Orders',      color:T.green},
  {key:'accessories',icon:'💳', label:'Accessories', color:T.teal},
  {key:'support',    icon:'🎫', label:'Support',     color:T.red},
  {key:'flapcare',   icon:'🪙', label:'FlapCare',    color:T.amber},
];

export default function SuperAdmin() {
  const navigate   = useNavigate();
  const [tab, setTab]         = useState('overview');
  const [authed, setAuthed]   = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth<800);

  useEffect(()=>{
    const fn=()=>setIsMobile(window.innerWidth<800);
    window.addEventListener('resize',fn);return()=>window.removeEventListener('resize',fn);
  },[]);

  useEffect(()=>{
    const token=localStorage.getItem('token');
    if(!token){navigate('/login');return;}
    try{
      const p=JSON.parse(atob(token.split('.')[1]));
      if(!['superadmin','admin'].includes(p.role)){navigate('/dashboard');return;}
      setIsSuperAdmin(p.role==='superadmin');
      setAuthed(true);
    }catch{navigate('/login');}
  },[navigate]);

  if(!authed) return null;
  const logout=()=>{localStorage.removeItem('token');navigate('/login');};

  const CONTENT={
    overview:   <OverviewTab/>,
    users:      <UsersTab isSuperAdmin={isSuperAdmin}/>,
    products:   <ProductsTab/>,
    orders:     <OrdersTab/>,
    accessories:<AccessoriesTab/>,
    support:    <SupportTab/>,
    flapcare:   <FlapCareTab/>,
  };

  const activeTab = SA_TABS.find(t=>t.key===tab);

  return (
    <div style={{display:'flex',minHeight:'100vh',background:T.bg,
      color:T.text,fontFamily:'DM Sans,sans-serif'}}>
      <style>{`
        ${FONTS}
        *{box-sizing:border-box;}
        ::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.1);border-radius:99px;}
        select option{background:#fff;color:#111;}
      `}</style>

      {/* Background */}
      <div style={{position:'fixed',inset:0,pointerEvents:'none',zIndex:0,
        backgroundImage:`linear-gradient(rgba(56,189,248,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,0.03) 1px,transparent 1px)`,
        backgroundSize:'48px 48px'}}/>

      {/* Mobile overlay */}
      {isMobile&&sideOpen&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',zIndex:90}}
          onClick={()=>setSideOpen(false)}/>
      )}

      {/* Sidebar */}
      <nav style={{
        width:isMobile?240:220,flexShrink:0,height:'100vh',position:isMobile?'fixed':'sticky',
        top:0,left:0,background:'rgba(255,255,255,0.98)',
        borderRight:`1px solid ${T.border}`,
        display:'flex',flexDirection:'column',overflowY:'auto',zIndex:100,
        transform:isMobile&&!sideOpen?'translateX(-100%)':'translateX(0)',
        transition:'transform .25s ease',boxShadow:isMobile?'4px 0 20px rgba(0,0,0,0.08)':'none',
      }}>
        {/* Logo */}
        <div style={{padding:'16px 14px 14px',borderBottom:`1px solid ${T.dim}`,flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:9}}>
            <div style={{width:30,height:30,borderRadius:9,
              background:`linear-gradient(135deg,${T.amber},#d97706)`,
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:14,fontWeight:900,color:'#fff',flexShrink:0}}>F</div>
            <div>
              <div style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:16,color:T.text}}>Flap</div>
              <div style={{fontSize:9,color:T.muted,letterSpacing:2,textTransform:'uppercase',
                fontFamily:'JetBrains Mono,monospace'}}>Admin</div>
            </div>
          </div>
          {isSuperAdmin&&<div style={{marginTop:8}}>
            <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:99,
              background:`rgba(${rgb(T.amber)},.12)`,color:T.amber,letterSpacing:.8,
              border:`1px solid rgba(${rgb(T.amber)},.2)`,fontFamily:'JetBrains Mono,monospace'}}>
              SUPERADMIN
            </span>
          </div>}
        </div>

        {/* Nav */}
        <div style={{padding:'8px 0',flex:1}}>
          {SA_TABS.map(t=>(
            <button key={t.key}
              onClick={()=>{setTab(t.key);setSideOpen(false);}}
              style={{display:'flex',alignItems:'center',gap:9,padding:'9px 12px',borderRadius:10,
                margin:'2px 8px',width:'calc(100% - 16px)',border:'none',cursor:'pointer',fontFamily:'inherit',
                background:tab===t.key?`rgba(${rgb(t.color)},.08)`:'transparent',
                color:tab===t.key?t.color:T.muted,
                outline:tab===t.key?`1px solid rgba(${rgb(t.color)},.2)`:'none',
                transition:'all .15s',fontSize:13,fontWeight:tab===t.key?700:500}}>
              <span style={{fontSize:15,width:20,textAlign:'center'}}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
          {/* Divider */}
          <div style={{height:1,background:T.dim,margin:'8px 12px'}}/>
          {[{icon:'🔙',label:'Dashboard',path:'/dashboard'},{icon:'👤',label:'My Profile',path:'/profile'}].map(it=>(
            <button key={it.path} onClick={()=>navigate(it.path)}
              style={{display:'flex',alignItems:'center',gap:9,padding:'9px 12px',borderRadius:10,
                margin:'2px 8px',width:'calc(100% - 16px)',border:'none',cursor:'pointer',
                background:'transparent',color:T.muted,fontFamily:'DM Sans,sans-serif',fontSize:13}}>
              <span style={{fontSize:15,width:20,textAlign:'center'}}>{it.icon}</span>
              <span>{it.label}</span>
            </button>
          ))}
        </div>

        {/* Logout */}
        <div style={{padding:'10px 10px 14px',borderTop:`1px solid ${T.dim}`,flexShrink:0}}>
          <button onClick={logout}
            style={{display:'flex',alignItems:'center',gap:8,width:'100%',
              padding:'9px 12px',borderRadius:10,border:'1px solid rgba(248,113,113,0.2)',
              background:'rgba(248,113,113,0.05)',color:T.red,cursor:'pointer',
              fontSize:13,fontWeight:600,fontFamily:'DM Sans,sans-serif'}}>
            ⏻ Log Out
          </button>
        </div>
      </nav>

      {/* Main */}
      <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',
        position:'relative',zIndex:1}}>
        {/* Shared Navbar — consistent top nav across all pages */}
        <Navbar/>
        {/* Page title bar — shows current admin section */}
        <div style={{padding:'10px 20px',background:'rgba(240,243,255,0.97)',
          borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',
          justifyContent:'space-between',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {isMobile&&<button onClick={()=>setSideOpen(s=>!s)}
              style={{width:32,height:32,borderRadius:8,border:`1px solid ${T.dim}`,
                background:'rgba(0,0,0,0.03)',color:T.text,cursor:'pointer',
                fontSize:15,display:'flex',alignItems:'center',justifyContent:'center'}}>☰</button>}
            <div>
              <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,
                fontSize:14,color:activeTab?.color||T.text}}>
                {activeTab?.icon} {activeTab?.label}
              </div>
              <div style={{fontSize:10,color:T.muted,fontFamily:'JetBrains Mono,monospace',
                textTransform:'uppercase',letterSpacing:1}}>
                Admin Panel · {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:'auto',padding:isMobile?'14px':'22px 28px'}}>
          {CONTENT[tab]}
        </div>
      </div>
    </div>
  );
}